import { useEffect, useMemo, useState } from 'react';
import { IconCash, IconArrowDownRight, IconArrowUpRight, IconAlertCircle, IconLock } from '@tabler/icons-react';
import Modal from './ui/Modal';
import { useManagerApproval } from '../hooks/useManagerApproval';

/**
 * DrawerModal — Phase 2.4.
 *
 * Records a paid-in or paid-out cash movement against the open day
 * session. Both directions require a reason; paid-out also requires
 * a fresh manager-PIN step-up before the request is even sent
 * (server enforces it too — defence in depth).
 *
 * SUPERVISOR+ only. Visibility is gated by the caller via RequireMinRole.
 */

type Direction = 'PAID_IN' | 'PAID_OUT';

interface Props {
  open: boolean;
  onClose: () => void;
  onRecorded?: (movement: { id: string; direction: Direction; amountCents: number }) => void;
}

function fmtR(cents: number) {
  return `R ${(cents / 100).toFixed(2)}`;
}

export default function DrawerModal({ open, onClose, onRecorded }: Props) {
  const [direction, setDirection] = useState<Direction>('PAID_IN');
  const [amount, setAmount] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const askApproval = useManagerApproval();

  useEffect(() => {
    if (open) {
      setDirection('PAID_IN');
      setAmount('');
      setReason('');
      setSubmitting(false);
      setError(null);
    }
  }, [open]);

  const amountCents = useMemo(() => {
    const v = Number(amount.replace(',', '.'));
    if (!isFinite(v) || v <= 0) return 0;
    return Math.round(v * 100);
  }, [amount]);

  const canSubmit = !submitting && amountCents > 0 && reason.trim().length > 0;

  async function submit() {
    if (!canSubmit) return;
    setError(null);

    // Paid-out requires manager-PIN step-up FIRST. Paid-in does not.
    if (direction === 'PAID_OUT') {
      const ok = await askApproval({
        action: `Paid out ${fmtR(amountCents)}`,
        description: 'Cash leaving the drawer needs manager approval.',
      });
      if (!ok) return;
    }

    setSubmitting(true);
    try {
      const r = await fetch('/api/drawer-movements', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ direction, amountCents, reason: reason.trim() }),
      });
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.error ?? `HTTP ${r.status}`);
      }
      const data = await r.json();
      onRecorded?.(data.movement);
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  const isPaidIn = direction === 'PAID_IN';
  const DirectionIcon = isPaidIn ? IconArrowDownRight : IconArrowUpRight;

  return (
    <Modal open={open} onClose={submitting ? () => {} : onClose} size="lg" title="Cash drawer">
      {/* Direction toggle */}
      <p className="text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">Movement type</p>
      <div className="grid grid-cols-2 gap-3 mb-5">
        <button
          type="button"
          onClick={() => setDirection('PAID_IN')}
          className={[
            'h-20 rounded-lg border-2 flex flex-col items-center justify-center gap-1 transition-all',
            isPaidIn
              ? 'bg-success text-[#FFFFFF] border-success shadow-md'
              : 'bg-surface border-border text-heading hover:border-success',
          ].join(' ')}
        >
          <IconArrowDownRight size={22} stroke={2} />
          <span className="text-14 font-bold">Paid in</span>
        </button>
        <button
          type="button"
          onClick={() => setDirection('PAID_OUT')}
          className={[
            'h-20 rounded-lg border-2 flex flex-col items-center justify-center gap-1 transition-all',
            !isPaidIn
              ? 'bg-danger text-[#FFFFFF] border-danger shadow-md'
              : 'bg-surface border-border text-heading hover:border-danger',
          ].join(' ')}
        >
          <IconArrowUpRight size={22} stroke={2} />
          <span className="text-14 font-bold">Paid out</span>
        </button>
      </div>

      {/* Amount */}
      <div className="mb-4">
        <label className="block text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">Amount (R)</label>
        <input
          type="text"
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9.,]/g, ''))}
          placeholder="0.00"
          className="w-full h-14 px-4 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-22 font-bold tabular-nums text-heading"
        />
        <div className="grid grid-cols-5 gap-2 mt-3">
          {[20, 50, 100, 200, 500].map((rand) => (
            <button
              key={rand}
              type="button"
              onClick={() => setAmount(String(rand))}
              className="h-10 rounded-md bg-light border border-border text-14 font-bold text-heading hover:border-primary hover:text-primary transition-colors"
            >
              R {rand}
            </button>
          ))}
        </div>
      </div>

      {/* Reason */}
      <div className="mb-4">
        <label className="block text-13 font-bold uppercase tracking-wider text-gray-700 mb-2">
          Reason <span className="text-danger">*</span>
        </label>
        <input
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={isPaidIn ? 'e.g. owner top-up float' : 'e.g. milk run, supplier cash'}
          className="w-full h-12 px-4 rounded-md bg-surface border-2 border-border focus:border-primary outline-none text-15 font-semibold text-heading"
        />
      </div>

      {/* Paid-out warning */}
      {!isPaidIn && (
        <div className="mb-4 rounded-md bg-warning-soft border border-warning/40 p-3 flex items-start gap-2">
          <IconLock size={18} className="text-warning flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-warning">
            Manager PIN is required to release cash from the drawer.
          </p>
        </div>
      )}

      {error && (
        <div className="mb-4 rounded-md bg-danger-soft border border-danger/30 p-3 flex items-start gap-2">
          <IconAlertCircle size={18} className="text-danger flex-shrink-0 mt-0.5" />
          <p className="text-13 font-semibold text-danger">{error}</p>
        </div>
      )}

      <div className="flex items-stretch gap-2">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="flex-1 h-12 rounded-md bg-light border border-border text-14 font-bold text-heading hover:bg-gray-100 disabled:opacity-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!canSubmit}
          className={[
            'flex-[2] h-12 rounded-md text-[#FFFFFF] text-15 font-bold transition-colors shadow-md inline-flex items-center justify-center gap-2 disabled:bg-gray-300 disabled:cursor-not-allowed',
            isPaidIn ? 'bg-success hover:bg-success-hover' : 'bg-danger hover:bg-danger-hover',
          ].join(' ')}
        >
          <DirectionIcon size={18} stroke={2.5} />
          {submitting ? 'Recording…' : `${isPaidIn ? 'Pay in' : 'Pay out'} ${amountCents > 0 ? fmtR(amountCents) : ''}`}
        </button>
      </div>

      {/* Small icon footer for visual identity */}
      <div className="text-center mt-5 opacity-40">
        <IconCash size={20} stroke={1.5} className="inline" />
      </div>
    </Modal>
  );
}
